import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import { usePageTitle } from "context/PageTitleContext";
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { getToken } from "services/auth.service";
import { useAuth } from "context/AuthContext";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import "./RolePermissionManagement.css";
import { getResourceDisplayName, getActionColor } from "modules/systems/utils/permission.utils";
import { AppButton } from "components/common/buttons";
import { AppTextField } from "components/common/inputs";

const RolePermissionManagement = () => {
  const { user } = useAuth();
  const { setPageTitle } = usePageTitle();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [applications, setApplications] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchApplication, setSearchApplication] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [saving, setSaving] = useState(false);
  const [dirtyRoleIds, setDirtyRoleIds] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [canCreateRolePermission, setCanCreateRolePermission] = useState(false);

  // Set page title
  useEffect(() => {
    setPageTitle("Phân quyền");
  }, [setPageTitle]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && user.role === "Admin") {
      setCanCreateRolePermission(true);
    } else if (user) {
      checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN).then((result) => {
        setCanCreateRolePermission(result.hasPermission === true);
      });
    } else {
      setCanCreateRolePermission(false);
    }
  }, [user]);

  useEffect(() => {
    if (permissions.length > 0) {
      groupPermissions();
    }
  }, [permissions, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedApplicationId && applications.length > 0) {
      setSelectedApplicationId(applications[0].applicationId);
    }
  }, [applications, selectedApplicationId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadApplications(),
        loadRoles(),
        loadPermissions(),
        loadRolePermissions(),
      ]);
    } catch (error) {
      showSnackbar("Lỗi khi tải dữ liệu", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Application/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setApplications(data.data || []);
      } else {
        console.error("API Error:", response.status, response.statusText);
      }
    } catch (error) {
      console.error("Error loading applications:", error);
    }
  };

  // Hàm xác định thứ tự ưu tiên của vai trò
  const getRoleOrder = (roleName) => {
    const roleOrder = {
      Admin: 1,
      Operator: 2,
      Creator: 3,
      Editor: 4,
      Viewer: 5,
    };
    return roleOrder[roleName] || 999; // Những vai trò khác sẽ được xếp cuối
  };

  const getApplicationKind = (application) => {
    const value = `${application?.applicationCode || ""} ${application?.accessPermissionCode || ""} ${application?.applicationName || ""} ${application?.description || ""}`.toLowerCase();
    if (value.includes("subcontract") || value.includes("giao kho")) return "subcontract";
    if (value.includes("equipment") || value.includes("thiet bi") || value.includes("thi")) return "equipment";
    if (value.includes("system") || value.includes("he thong") || value.includes("hệ thống")) return "system";
    if (value.includes("contract") || value.includes("hop dong") || value.includes("hợp đồng")) return "contract";
    return "";
  };

  const getApplicationModuleCode = (application) => {
    const accessCode =
      application?.accessPermissionCode ||
      application?.AccessPermissionCode ||
      application?.applicationCode ||
      application?.ApplicationCode ||
      "";
    const codeParts = String(accessCode).toLowerCase().split(".");

    if (codeParts[0] === "pms" && codeParts.length > 1) {
      return codeParts[1];
    }

    return codeParts[0] || getApplicationKind(application);
  };

  const getRoleModuleCode = (role) => {
    const roleCode = role?.roleCode || role?.RoleCode || "";
    const codeParts = String(roleCode).toLowerCase().split(".");
    return codeParts[0] === "pms" ? codeParts[1] || "" : codeParts[0] || "";
  };

  const roleMatchesApplication = (role, application) => {
    if (!application) return true;

    const roleApplicationId = role.applicationId ?? role.ApplicationId;
    if (roleApplicationId) {
      return Number(roleApplicationId) === Number(application.applicationId);
    }

    const roleModuleCode = getRoleModuleCode(role);
    if (roleModuleCode) {
      return roleModuleCode === getApplicationModuleCode(application);
    }

    return true;
  };

  const getPermissionApplicationKind = (permission) => {
    return (
      permission.moduleCode ||
      permission.ModuleCode ||
      permission.permissionCode?.split(".")[0] ||
      permission.permissionName?.split(".")[0] ||
      ""
    ).toLowerCase();
  };

  const selectedApplication = useMemo(
    () =>
      applications.find(
        (application) =>
          Number(application.applicationId) === Number(selectedApplicationId)
      ),
    [applications, selectedApplicationId]
  );

  const filteredApplications = useMemo(
    () =>
      applications.filter(
        (application) =>
          application.applicationName
            ?.toLowerCase()
            .includes(searchApplication.toLowerCase()) ||
          application.description
            ?.toLowerCase()
            .includes(searchApplication.toLowerCase()) ||
          application.applicationCode
            ?.toLowerCase()
            .includes(searchApplication.toLowerCase())
      ),
    [applications, searchApplication]
  );

  const applicationPermissions = useMemo(() => {
    const selectedKind = getApplicationKind(selectedApplication);

    return permissions.filter((permission) => {
      const permissionType = permission.permissionType || permission.PermissionType;
      const matchesApplicationId =
        (permission.applicationId ?? permission.ApplicationId) &&
        Number(permission.applicationId ?? permission.ApplicationId) === Number(selectedApplicationId);
      const matchesApplicationKind =
        selectedKind && getPermissionApplicationKind(permission) === selectedKind;
      const matchesSearch =
        !searchTerm ||
        (permission.permissionCode || permission.permissionName)
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (permission.permissionDisplayName || permission.description)
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        permission.resourceCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.actionCode?.toLowerCase().includes(searchTerm.toLowerCase());

      return permissionType !== "Application" && (matchesApplicationId || matchesApplicationKind) && matchesSearch;
    });
  }, [permissions, searchTerm, selectedApplication, selectedApplicationId]);

  const applicationRoles = useMemo(() => {
    return roles
      .filter((role) => roleMatchesApplication(role, selectedApplication))
      .sort((a, b) => getRoleOrder(a.roleName) - getRoleOrder(b.roleName) || a.roleId - b.roleId);
  }, [roles, selectedApplication]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRoles = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/RolesManagement?pageSize=1000&pageNumber=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        // Sắp xếp vai trò theo thứ tự: Admin > Operator > Creator > Editor > Viewer
        const sortedRoles = (data.data || []).sort((a, b) => {
          return getRoleOrder(a.roleName) - getRoleOrder(b.roleName);
        });
        setRoles(sortedRoles);
      } else {
        console.error("API Error:", response.status, response.statusText);
      }
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  const loadPermissions = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/SystemPermissions/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPermissions(
          (data.data || []).filter(
            (permission) =>
              (permission.permissionType || permission.PermissionType) !== "Application"
          )
        );
      } else {
        console.error("API Error:", response.status, response.statusText);
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
    }
  };

  const loadRolePermissions = async () => {
    try {
      const token = getToken();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/RolePermission?pageSize=1000&pageNumber=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();


        const rolePermissionsMap = {};

        // Group by role
        (data.data || []).forEach((rp) => {

          if (!rolePermissionsMap[rp.roleId]) {
            rolePermissionsMap[rp.roleId] = [];
          }
          rolePermissionsMap[rp.roleId].push(rp.permissionId);
        });


        setRolePermissions(rolePermissionsMap);
      } else {
        console.error("API Error:", response.status, response.statusText);
      }
    } catch (error) {
      console.error("Error loading role permissions:", error);
    }
  };

  const groupPermissions = () => {
    const filtered = permissions.filter(
      (permission) =>
        permission.permissionName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        permission.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const grouped = {};

    filtered.forEach((permission) => {
      const resource =
        permission.resourceCode ||
        permission.ResourceCode ||
        permission.permissionCode?.split(".")[1] ||
        permission.permissionName?.split(".")[1] ||
        "other";
      const field = permission.moduleCode || permission.ModuleCode || "all";
      const action =
        permission.actionCode ||
        permission.ActionCode ||
        permission.permissionCode?.split(".")[2] ||
        permission.permissionName?.split(".")[2] ||
        permission.permissionName;

      if (!grouped[resource]) {
        grouped[resource] = {};
      }
      if (!grouped[resource][field]) {
        grouped[resource][field] = [];
      }
      grouped[resource][field].push({
        ...permission,
        action,
      });
    });

    setGroupedPermissions(grouped);
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const isPermissionChecked = (permissionId) => {
    if (!selectedRole || !rolePermissions[selectedRole.roleId]) {
      return false;
    }
    return rolePermissions[selectedRole.roleId].includes(permissionId);
  };

  const handlePermissionToggle = (permissionId, checked) => {
    if (!selectedRole) return;

    const roleId = selectedRole.roleId;
    const currentPermissions = rolePermissions[roleId] || [];

    let updatedPermissions;
    if (checked) {
      updatedPermissions = [...currentPermissions, permissionId];
    } else {
      updatedPermissions = currentPermissions.filter(
        (id) => id !== permissionId
      );
    }

    setRolePermissions({
      ...rolePermissions,
      [roleId]: updatedPermissions,
    });
  };

  const handleRolePermissionToggle = (role, permissionId, checked) => {
    const roleId = role.roleId;
    const currentPermissions = rolePermissions[roleId] || [];
    const updatedPermissions = checked
      ? Array.from(new Set([...currentPermissions, permissionId]))
      : currentPermissions.filter((id) => id !== permissionId);

    setRolePermissions({
      ...rolePermissions,
      [roleId]: updatedPermissions,
    });
    setDirtyRoleIds((current) =>
      current.includes(roleId) ? current : [...current, roleId]
    );
  };

  const isRolePermissionChecked = (roleId, permissionId) =>
    (rolePermissions[roleId] || []).includes(permissionId);

  const permissionSections = useMemo(() => {
    const sections = {};

    applicationPermissions.forEach((permission) => {
      const resource =
        permission.resourceCode ||
        permission.ResourceCode ||
        permission.permissionCode?.split(".")[1] ||
        permission.permissionName?.split(".")[1] ||
        "other";
      const action =
        permission.actionCode ||
        permission.ActionCode ||
        permission.permissionCode?.split(".")[2] ||
        permission.permissionName?.split(".")[2] ||
        permission.permissionName;

      if (!sections[resource]) {
        sections[resource] = [];
      }

      sections[resource].push({
        ...permission,
        action,
      });
    });

    return Object.entries(sections).map(([resource, sectionPermissions]) => ({
      resource,
      title: getResourceDisplayName(resource),
      permissions: sectionPermissions.sort((left, right) =>
        String(left.displayOrder || left.permissionName || "").localeCompare(
          String(right.displayOrder || right.permissionName || ""),
          "vi",
          { sensitivity: "base" }
        )
      ),
    }));
  }, [applicationPermissions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveRolePermissions = async () => {
    const roleIdsToSave = dirtyRoleIds.length
      ? dirtyRoleIds
      : selectedRole
        ? [selectedRole.roleId]
        : [];

    if (roleIdsToSave.length === 0) return;

    // Kiểm tra quyền
    if (!canCreateRolePermission) {
      showSnackbar("Bạn không có quyền lưu quyền cho vai trò", "warning");
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      const roleId = selectedRole.roleId;
      const permissionIds = rolePermissions[roleId] || [];

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/RolePermission/role/${roleId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            roleId: roleId,
            permissionIds: permissionIds,
          }),
        }
      );

      if (response.ok) {
        showSnackbar(
          `Đã lưu quyền cho role "${selectedRole.roleName}" thành công`,
          "success"
        );
        await loadRolePermissions(); // Reload to ensure data consistency
      } else {
        throw new Error("Failed to save permissions");
      }
    } catch (error) {
      showSnackbar("Lỗi khi lưu quyền", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMatrixRolePermissions = async () => {
    if (dirtyRoleIds.length === 0) return;

    if (!canCreateRolePermission) {
      showSnackbar("Bạn không có quyền cho vai trò", "warning");
      return;
    }

    setSaving(true);
    try {
      const token = getToken();

      for (const roleId of dirtyRoleIds) {
        const permissionIds = rolePermissions[roleId] || [];
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/RolePermission/role/${roleId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              roleId,
              permissionIds,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to save permissions");
        }
      }

      showSnackbar("Lưu phân quyền vai trò thành công", "success");
      setDirtyRoleIds([]);
      await loadRolePermissions();
    } catch (error) {
      showSnackbar("Lỗi khi lưu phân quyền", "error");
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  // const getActionColor = (action) => {
  //   const colors = {
  //     read: "primary",
  //     create: "success",
  //     update: "warning",
  //     delete: "error",
  //     import: "info",
  //     export: "secondary",
  //     submit: "info",
  //     approve: "secondary",
  //     assign: "warning",
  //     access: "primary",
  //   };
  //   return colors[action] || "default";
  // };

  // function getResourceDisplayName(resource) {
  //   const displayNames = {
  //     contract: "Hợp đồng",
  //     contract_group: "Nhóm hợp đồng",
  //     contract_history: "Lịch sử hợp đồng",
  //     contract_item: "Hạng mục hợp đồng",
  //     contract_payment: "Thanh toán hợp đồng",
  //     client: "Khách hàng",
  //     contractor: "Đơn vị thi công",
  //     contracthistory: "Lịch sử hợp đồng",
  //     contractitem: "Hạng mục hợp đồng",
  //     contractpayment: "Thanh toán hợp đồng",
  //     contractstatushistory: "Lịch sử trạng thái hợp đồng",
  //     personnel: "Nhân sự",
  //     department: "Phòng ban",
  //     listitemdocumenttype: "Loại tài liệu",
  //     listitempositionindepartment: "Chức vụ trong phòng ban",
  //     listitemroleincontract: "Vai trò trong hợp đồng",
  //     rolepermission: "Phân quyền",
  //     permission_function: "Phân quyền chức năng",
  //     permission_application: "Phân quyền ứng dụng",
  //     userloginhistory: "Lịch sử đăng nhập",
  //     user: "Tài khoản người dùng",
  //     document_type: "Danh mục tài liệu",
  //     department_unit: "Đội trong phòng ban",
  //     release_log: "Nhật ký phát hành",
  //     subcontract_group: "Loại giao khoán",
  //     subcontract_execution_group: "Nhóm thực hiện giao khoán",
  //     subcontract_execution_group_user: "Nhân sự nhóm giao khoán",
  //     subcontractdashboard: "Tổng quan quyết định giao khoán",
  //     subcontract: "Quyết định giao khoán",
  //     subcontract_item: "Dự toán",
  //     subcontract_history: "Lịch sử giao khoán",
  //     subcontract_approval: "Phê duyệt giao khoán",
  //     subcontract_acceptance: "Nghiệm thu giao khoán",
  //     subcontract_payment: "Thanh toán giao khoán",
  //     acceptance_approval: "Phê duyệt nghiệm thu",
  //     draft_item: "Hạng mục dự toán nháp",
  //     dashboard: "Tổng quan",
  //     statistics: "Thống kê",
  //     notification: "Thông báo",
  //     calendar: "Lịch biểu",
  //     subcontractacceptance: "Nghiệm thu quyết định giao khoán",
  //     subcontractapproval: "Phê duyệt quyết định giao khoán",
  //     subcontractpayment: "Thanh toán quyết định giao khoán",
  //     subcontractstatistical: "Thống kê quyết định giao khoán",
  //     other: "Khác",
  //   };
  //   return displayNames[resource] || resource;
  // }

  const getSelectedRolePermissionCount = () => {
    if (!selectedRole || !rolePermissions[selectedRole.roleId]) {
      return 0;
    }
    return rolePermissions[selectedRole.roleId].length;
  };

  return (
    <Box className="role-permission-management tab-content">
      {/* <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: "bold", color: "#1976d2" }}
      >
        <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Quản lý phân quyền theo vai trò
      </Typography> */}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <SecurityIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Ứng dụng
              </Typography>
            </Box>
            <AppTextField
              fullWidth
              placeholder="Tìm kiếm ứng dụng..."
              value={searchApplication}
              onChange={(value) => setSearchApplication(value)}
              sx={{ mb: 2 }}
              startIcon={<SearchIcon fontSize="small" />}
            />
            <Box sx={{ display: "grid", gap: 1 }}>
              {filteredApplications.map((application) => {
                const isSelected =
                  Number(application.applicationId) === Number(selectedApplicationId);

                return (
                  <Box
                    key={application.applicationId}
                    component="button"
                    type="button"
                    onClick={() => {
                      setSelectedApplicationId(application.applicationId);
                      setDirtyRoleIds([]);
                    }}
                    sx={{
                      p: 1.5,
                      textAlign: "left",
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      borderRadius: 1,
                      bgcolor: isSelected ? "#e3f2fd" : "#fff",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <Typography fontWeight={700}>
                      {application.applicationName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {application.description || application.applicationCode}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {selectedApplication?.applicationName || "Chọn ứng dụng"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {applicationRoles.length} vai trò - {applicationPermissions.length} quyền
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Tải lại dữ liệu">
                  <AppButton color="inherit" onClick={loadData}><RefreshIcon /></AppButton>
                </Tooltip>
                {canCreateRolePermission && (
                  <AppButton
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveMatrixRolePermissions}
                    disabled={saving || dirtyRoleIds.length === 0}
                    sx={{ textTransform: "none" }}
                  >
                    {saving ? "Đang lưu..." : `Phân quyền${dirtyRoleIds.length ? ` (${dirtyRoleIds.length})` : ""}`}
                  </AppButton>
                )}
              </Box>
            </Box>

            {/* <TextField
              fullWidth
              size="small"
              placeholder="Tìm kiếm quyền..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            /> */}
            <AppTextField
              placeholder="Tìm kiếm quyền ..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              sx={{ flex: 1, mb: 2 }}
              startIcon={<SearchIcon fontSize="small" />}
            />

            <Box sx={{ display: "grid", gap: 2, maxHeight: 640, overflow: "auto", pr: 0.5 }}>
              {permissionSections.length === 0 ? (
                <Alert severity="info">Không có quyền phù hợp trong ứng dụng đang chọn.</Alert>
              ) : (
                permissionSections.map((section) => (
                  <Box
                    key={section.resource}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      overflow: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: `minmax(200px, 1fr) repeat(${applicationRoles.length}, minmax(130px, 1fr))`,
                        bgcolor: "#f8f9fa",
                        borderBottom: "1px solid #e0e0e0",
                        minWidth: 200 + applicationRoles.length * 130,
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          color="primary"
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            fontSize: "14px",
                            fontWeight: 700,
                          }}
                        >
                          {section.title}
                        </Typography>
                      </Box>
                      {applicationRoles.map((role) => (
                        <Box
                          key={role.roleId}
                          sx={{ p: 1.25, fontWeight: 500, textAlign: "center", fontSize: 12 }}
                        >
                          <Typography fontWeight={650} fontSize="12px">
                            {role.roleName}
                          </Typography>
                          {/* {role.roleCode && (
                            <Typography variant="caption" color="text.secondary">
                              {role.roleCode}
                            </Typography>
                          )} */}
                        </Box>
                      ))}
                    </Box>
                    {section.permissions.map((permission) => (
                      <Box
                        key={`${section.resource}-${permission.permissionId}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: `minmax(200px, 1fr) repeat(${applicationRoles.length}, minmax(130px, 1fr))`,
                          minWidth: 200 + applicationRoles.length * 130,
                          borderBottom: "1px solid #e0e0e0",
                          "&:last-child": { borderBottom: 0 },
                        }}
                      >
                        <Box sx={{ p: 1.25 }}>
                          <Typography fontWeight={650} fontSize="13px">
                            {permission.permissionDisplayName || permission.description || permission.permissionName}
                          </Typography>
                          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <Chip
                              label={permission.action}
                              // size="small"
                              color={getActionColor(permission.action)}
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                "& .MuiChip-label": {
                                  px: 0.75,
                                },
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {permission.permissionCode || permission.permissionName}
                            </Typography>
                          </Box> */}
                        </Box>
                        {applicationRoles.map((role) => (
                          <Box
                            key={`${role.roleId}-${permission.permissionId}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: 48,
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={isRolePermissionChecked(role.roleId, permission.permissionId)}
                              disabled={saving || !canCreateRolePermission}
                              onChange={(event) =>
                                handleRolePermissionToggle(
                                  role,
                                  permission.permissionId,
                                  event.target.checked
                                )
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* UI cũ giữ lại để tham chiếu. */}
      {false && (
        <Grid container spacing={3}>
          {/* Left Panel - Roles List */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <GroupIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Danh sách vai trò
                </Typography>
              </Box>

              {roles.map((role) => (
                <Box
                  key={role.roleId}
                  onClick={() => handleRoleSelect(role)}
                  className={`role-item ${selectedRole?.roleId === role.roleId ? "selected" : ""
                    }`}
                  sx={{
                    p: 2,
                    mb: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    backgroundColor:
                      selectedRole?.roleId === role.roleId ? "#e3f2fd" : "#fff",
                    "&:hover": {
                      backgroundColor:
                        selectedRole?.roleId === role.roleId
                          ? "#e3f2fd"
                          : "#f5f5f5",
                      borderColor: "#1976d2",
                    },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {role.roleName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {role.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {role.roleName === "Admin"
                      ? "Quyền: Toàn quyền"
                      : `Quyền: ${rolePermissions[role.roleId]?.length || 0}`}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Right Panel - Permissions */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {selectedRole
                    ? selectedRole.roleName === "Admin"
                      ? `Quyền của vai trò: ${selectedRole.roleName} (Toàn quyền)`
                      : `Quyền của vai trò: ${selectedRole.roleName
                      } (${getSelectedRolePermissionCount()} quyền)`
                    : "Chọn vai trò để xem quyền"}
                </Typography>

                {selectedRole && selectedRole.roleName !== "Admin" && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Tải lại">
                      <IconButton onClick={loadData} size="small">
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    {canCreateRolePermission && (
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveRolePermissions}
                        disabled={saving}
                        sx={{ textTransform: "none" }}
                      >
                        {saving ? "Đang lưu..." : "Lưu quyền"}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {selectedRole && selectedRole.roleName === "Admin" && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <SecurityIcon sx={{ fontSize: 64, color: "#4caf50", mb: 2 }} />
                  <Typography
                    variant="h5"
                    color="primary"
                    sx={{ mb: 2, fontWeight: "bold" }}
                  >
                    Quyền Admin - Toàn quyền hệ thống
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Vai trò Admin có toàn quyền truy cập và quản lý mọi tính năng
                    trong hệ thống.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Không cần cấu hình quyền chi tiết cho vai trò này.
                  </Typography>
                </Box>
              )}

              {selectedRole && selectedRole.roleName !== "Admin" && (
                <>
                  {/* <TextField
                    fullWidth
                    size="small"
                    placeholder="Tìm kiếm quyền..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2 }}
                    slotProps={{
                      // <-- Thay thế InputProps bằng slotProps
                      input: {
                        // <-- Thêm key 'input' ở đây
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      },
                    }}
                  /> */}
                  <AppTextField
                    placeholder="Tìm kiếm quyền ..."
                    value={searchTerm}
                    onChange={(value) => setSearchTerm(value)}
                    sx={{ flex: 1, mb: 2 }}
                    startIcon={<SearchIcon fontSize="small" />}
                  />

                  <Box sx={{ maxHeight: 600, overflow: "auto" }}>
                    {Object.keys(groupedPermissions).map((resource) => (
                      <Accordion key={resource}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold" }}
                          >
                            {getResourceDisplayName(resource)}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {Object.keys(groupedPermissions[resource]).map(
                            (field) => (
                              <Box key={field} sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold", mb: 1 }}
                                >
                                  {/* {field === "all" ? "Tất cả trường" : field}: */}
                                  {field === "all" ? "Tất cả trường" : "Khác"}:
                                </Typography>
                                <Grid container spacing={1} sx={{ ml: 2 }}>
                                  {groupedPermissions[resource][field].map(
                                    (permission) => (
                                      <Grid
                                        size={{ xs: 12, sm: 6, md: 4 }}
                                        key={permission.permissionId}
                                      >
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={isPermissionChecked(
                                                permission.permissionId
                                              )}
                                              onChange={(e) =>
                                                handlePermissionToggle(
                                                  permission.permissionId,
                                                  e.target.checked
                                                )
                                              }
                                              size="small"
                                            />
                                          }
                                          label={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <Chip
                                                label={permission.action}
                                                size="small"
                                                color={getActionColor(
                                                  permission.action
                                                )}
                                              />
                                              <Typography variant="body2">
                                                {permission.description}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                      </Grid>
                                    )
                                  )}
                                </Grid>
                              </Box>
                            )
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </>
              )}

              {!selectedRole && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <SecurityIcon sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Chọn một vai trò để quản lý quyền
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolePermissionManagement;
